import { Injectable, inject } from '@angular/core';
import { LocalStorageService } from './local-storage.service';
import { AuthResponse } from '../Interfaces/response.interface';
import { AccessDecision } from '../../../Types/access-decision.type';

@Injectable({
  providedIn: 'root'
})
export class AuthTokenService {
  private _localStorage = inject(LocalStorageService);

  private readonly TOKEN_KEY = 'auth_token';
  private readonly REFRESH_TOKEN_KEY = 'auth_refresh_token';
  private readonly TOKEN_EXPIRY_KEY = 'auth_token_expiry';
  private readonly RETURN_URL_KEY = 'auth_return_url';

  setToken(auth_data: AuthResponse): void {
    const expiry_time = new Date().getTime() + (auth_data.expires_in * 1000);

    this._localStorage.setItem(this.TOKEN_KEY, auth_data.access_token);

    if (auth_data.refresh_token) {
      this._localStorage.setItem(this.REFRESH_TOKEN_KEY, auth_data.refresh_token);
    } else {
      this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    }

    this._localStorage.setItem(this.TOKEN_EXPIRY_KEY, expiry_time.toString());
  }

  getJwtPayload(): Record<string, unknown> | null {
    const token = this.getToken();

    if (!token) {
      return null;
    }

    const segments = token.split('.');

    if (segments.length !== 3) {
      return null;
    }

    try {
      const decoded = atob(segments[1].replace(/-/g, '+').replace(/_/g, '/'));
      return JSON.parse(decoded);
    } catch {
      return null;
    }
  }

  isImpersonated(): boolean {
    return this.getJwtPayload()?.['impersonated'] === true;
  }

  getImpersonatorEmail(): string | null {
    const email = this.getJwtPayload()?.['impersonator_email'];
    return typeof email === 'string' ? email : null;
  }

  getTokenExp(): number | null {
    const exp = this.getJwtPayload()?.['exp'];
    return typeof exp === 'number' ? exp : null;
  }

  getAccessDecision(): AccessDecision | null {
    const decision = this.getJwtPayload()?.['access_decision'];
    return typeof decision === 'string' ? (decision as AccessDecision) : null;
  }

  getToken(): string | null {
    return this._localStorage.getItem(this.TOKEN_KEY);
  }

  getRefreshToken(): string | null {
    return this._localStorage.getItem(this.REFRESH_TOKEN_KEY);
  }

  isTokenExpired(): boolean {
    const expiry = this._localStorage.getItem(this.TOKEN_EXPIRY_KEY);

    if (!expiry) {
      return true;
    }

    return new Date().getTime() > parseInt(expiry, 10);
  }

  hasValidToken(): boolean {
    return !!this.getToken() && !this.isTokenExpired();
  }

  hasRefreshToken(): boolean {
    return !!this.getRefreshToken();
  }

  clearToken(): void {
    this._localStorage.removeItem(this.TOKEN_KEY);
    this._localStorage.removeItem(this.REFRESH_TOKEN_KEY);
    this._localStorage.removeItem(this.TOKEN_EXPIRY_KEY);
  }

  setReturnUrl(returnUrl: string): void {
    this._localStorage.setItem(this.RETURN_URL_KEY, returnUrl);
  }

  getAndClearReturnUrl(): string {
    const returnUrl = this._localStorage.getItem(this.RETURN_URL_KEY) || '/';
    this._localStorage.removeItem(this.RETURN_URL_KEY);
    return returnUrl;
  }
}
